/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: EmailSettingsPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main email settings page with configuration management
    and SMTP service integration.

 ?? Notes:
    - Integrates EmailConfigForm for configuration management
    - Supports multiple email service providers
    - Implements comprehensive email settings management

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { EmailTestModal } from './EmailTestModal';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { useEmailSettingsList } from './hooks/useEmailSettingsList';
import { useEmailSettingsColumnRenderers } from './hooks/useEmailSettingsColumns';
import { EmailSettingsActions } from './components/EmailSettingsActions';
import { deleteEmailConfigById, navigateToEmailConfigDuplicate } from './emailSettingsActions';
import type { EmailConfig } from './types';

export function EmailSettingsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();

  const [showTestModal, setShowTestModal] = useState(false);
  const [testConfig, setTestConfig] = useState<EmailConfig | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<EmailConfig | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data: gridData,
    columns: gridColumns,
    loading: gridLoading,
    pagination: gridPagination,
    searchQuery: gridSearchQuery,
    sortBy: gridSortBy,
    sortOrder: gridSortOrder,
    handleSearch: gridHandleSearch,
    handleSort: gridHandleSort,
    handlePageChange: gridHandlePageChange,
    handleItemsPerPageChange: gridHandleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useEmailSettingsList();

  const columnRenderers = useEmailSettingsColumnRenderers();

  const handleCreateConfig = useCallback(() => {
    navigate('/admin/system-settings/email/new');
  }, [navigate]);

  const handleEditConfig = useCallback((config: EmailConfig) => {
    navigate(`/admin/system-settings/email/${config.uuid ?? config.id}`);
  }, [navigate]);

  const handleDuplicateConfig = useCallback((config: EmailConfig) => {
    navigateToEmailConfigDuplicate(navigate, config.uuid ?? config.id!);
  }, [navigate]);

  const handleDeleteConfig = useCallback((config: EmailConfig) => {
    setConfigToDelete(config);
    setShowDeleteModal(true);
  }, []);

  const handleTestConfig = useCallback((config: EmailConfig) => {
    setTestConfig(config);
    setShowTestModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!configToDelete) return;

    try {
      setIsDeleting(true);
      await deleteEmailConfigById(configToDelete.id!, user?.tenant?.slug || 'main', user?.id);
      setShowDeleteModal(false);
      setConfigToDelete(null);
      showSuccess(t('emailSettings.messages.deleteSuccess'));
      refresh();
    } catch (error) {
      showError(error?.message || t('emailSettings.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [configToDelete, user?.tenant?.slug, user?.id, t, refresh, showSuccess, showError]);

  const handleTestSuccess = useCallback(() => {
    setShowTestModal(false);
    setTestConfig(null);
    refresh();
  }, [refresh]);

  const columnsWithRender = useMemo(
    () =>
      gridColumns.map((col) => {
        if (col.key === 'authType') {
          return {
            ...col,
            render: columnRenderers.authType,
          };
        }

        if (col.key === 'isActive' || col.key === 'isDefault') {
          return {
            ...col,
            render: col.key === 'isActive' ? columnRenderers.isActive : columnRenderers.isDefault,
          };
        }

        if (col.key === 'actions') {
          return {
            ...col,
            render: (_: unknown, row: EmailConfig) => (
              <EmailSettingsActions
                config={row}
                onEdit={handleEditConfig}
                onDuplicate={handleDuplicateConfig}
                onTest={handleTestConfig}
                onDelete={handleDeleteConfig}
              />
            ),
          };
        }

        return col;
      }),
    [gridColumns, columnRenderers, handleDeleteConfig, handleDuplicateConfig, handleEditConfig, handleTestConfig]
  );

  return (
    <>
      <div className="p-6">
        <DataGridSimple
          data={gridData}
          columns={columnsWithRender}
          loading={gridLoading}
          onColumnToggle={handleColumnToggle}
          pagination={gridPagination}
          onPageChange={gridHandlePageChange}
          onItemsPerPageChange={gridHandleItemsPerPageChange}
          searchQuery={gridSearchQuery}
          onSearch={gridHandleSearch}
          sortBy={gridSortBy}
          sortOrder={gridSortOrder}
          onSort={gridHandleSort}
          onAdd={handleCreateConfig}
          onEdit={handleEditConfig}
          onDuplicate={handleDuplicateConfig}
          onDelete={handleDeleteConfig}
          title={t('emailSettings.title')}
          titleIcon={<Mail className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('common.searchPlaceholder')}
          emptyMessage={t('common.noItemsFound')}
          deleteTitle={t('emailSettings.deleteConfiguration')}
          deleteMessage={item => t('emailSettings.messages.confirmDelete', { name: item.name })}
          translations={{
            items: t('common.items'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            columns: t('common.columns'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('emailSettings.deleteConfiguration')}
        message={
          configToDelete
            ? t('emailSettings.messages.confirmDelete', { name: configToDelete.name })
            : ''
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        type="danger"
      />

      {/* Email Test Modal */}
      {showTestModal && testConfig && (
        <EmailTestModal
          config={testConfig}
          onClose={() => setShowTestModal(false)}
          onSuccess={handleTestSuccess}
        />
      )}
    </>
  );
}
